import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { disconnect } from '@wagmi/core';
import { ToastrService } from 'ngx-toastr';
import { LoginService } from 'src/app/services/login.service';
import { StorageService } from 'src/app/services/storage.service';
import { environment } from 'src/environments/environment';
import { wagmi_config } from '../config/wagmi_config';

@Injectable({
  providedIn: 'root'
})

/**
 * Idle Service for auto logout if the time(which has been set in environment) expires
 */
export class IdleService {
  private sessionToken = '';
  private adminId = '';
  private expiryTime = '';
  private isLoggingOut = false;



  /**
   * constructor
   */
  constructor(
    private idle: Idle,
    private storageService: StorageService,
    private router: Router,
    private loginService: LoginService,
    private toastr: ToastrService,
  ) {
    this.idle.setIdle(environment.AUTO_LOGOUT_DURATION);
    this.idle.setTimeout(environment.AUTO_LOGOUT_TIMEOUT);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.onIdleEnd.subscribe(() => this.reset());
    this.idle.onIdleStart.subscribe(() => {
    });
    /** Backup to stop idle timer if still running while user is not logged in (haven't seen this be needed yet) */
    this.idle.onTimeoutWarning.subscribe(() => {
      if (!this.isLoggedIn) {
        this.idle.stop();
      }
    });

    /** called after the inactive timeout */
    this.idle.onTimeout.subscribe(() => {
      this.storageService.clearItem();
    });
    if (this.isLoggedIn) {
      this.idle.watch();
    }
    this.checkSessionValidity();
  }

  private get isLoggedIn(): boolean {
    this.sessionToken = this.storageService.getItem('token') || '';
    this.adminId = this.storageService.getItem('adminId') || '';
    return !!this.sessionToken && !!this.adminId;
  }

  private checkSessionValidity(): void {
    const expiryTimeString = this.storageService.getItem('expiryTime');
    if (expiryTimeString) {
      try {
        this.expiryTime = JSON.parse(expiryTimeString);
      } catch (error) {
        console.error('Error parsing expiryTime:', error);
        this.storageService.removeItem('expiryTime');
        this.expiryTime = '';
      }
    } else {
      this.expiryTime = '';
    }
    const expirationDate = this.expiryTime;

    // Ensure the token expiration date exists
    if (expirationDate) {
      const currentTime = new Date().getTime();
      const expTime = new Date(expirationDate).getTime();

      // Check if the token is expired
      if (expTime <= currentTime) {
        this.handleSessionExpired();
      }
    }
  }

  /**
   * Ends an expired session: tells the admin, then runs the full logout
   * (server logout, disconnect wallet, clear storage, back to the login page).
   * The toast is shown only for a live session, once per logout, so the 401 the
   * logout call itself may return cannot produce a second one.
   * @returns {void}
   */
  public handleSessionExpired(): void {
    if (this.isLoggedIn && !this.isLoggingOut) {
      this.toastr.error(
        'Your session has expired. Please connect your wallet and sign in again.',
        'Session Expired'
      );
    }
    this.logoutAdmin();
  }


  /**
   * start idle if logged in & not running.
   */
  public reset(): void {
    if (this.isLoggedIn && !this.idle.isRunning()) {
      this.idle.watch();
    }
  }

  /**
   * logout the admin — invalidates the session on the server (best-effort),
   * disconnects the wallet and clears local state before routing to the login
   * page. Re-entrancy guarded so a 401 from the logout endpoint cannot
   * re-trigger this flow through the HTTP interceptor.
   */
  logoutAdmin() {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    const finalize = async () => {
      // Disconnect before clearing storage: wagmi reads the connected
      // connector back out of local storage.
      await disconnect(wagmi_config).catch(() => undefined);
      this.storageService.clearItem();
      this.idle.stop();
      this.router.navigate(['/login']);
      this.isLoggingOut = false;
    };

    if (this.isLoggedIn) {
      this.loginService.logout().subscribe({
        next: () => finalize(),
        error: () => finalize()
      });
    } else {
      finalize();
    }
  }


}
